// Chrome Extension Communication Handler

// Use existing @types/chrome package for type definitions

export interface ChromeExtensionMessage {
  type: 'CHROME_EXTENSION_INIT' | 'PAGE_CONTENT_SCRAPED';
  data?: any;
  isChromeExtension?: boolean;
}

export interface ScrapedPageContent {
  url: string;
  title: string;
  content: {
    documentStructure: any[];
    readableContent: string | { structured: any[]; flowing: string; wordCount: number };
    meta: {
      description?: string;
      keywords?: string;
      author?: string;
      robots?: string;
      // Legacy fields that might exist
      title?: string;
      url?: string;
      domain?: string;
      timestamp?: string;
    };
    context: any;
  };
  timestamp?: string;
  source?: string;
}

export class ChromeExtensionHandler {
  private static instance: ChromeExtensionHandler;
  private listeners: Set<(message: ChromeExtensionMessage) => void> = new Set();
  private isChromeExtension = false;
  private isInitialized = false;

  private constructor() {
    this.init();
  }

  public static getInstance(): ChromeExtensionHandler {
    if (!ChromeExtensionHandler.instance) {
      ChromeExtensionHandler.instance = new ChromeExtensionHandler();
    }
    return ChromeExtensionHandler.instance;
  }

  private init() {
    if (this.isInitialized) return;

    // Listen for messages from Chrome extension
    window.addEventListener('message', this.handleMessage.bind(this));

    // Check if we're in an iframe (potential Chrome extension)
    this.detectChromeExtension();

    this.isInitialized = true;
    console.log('Chrome Extension Handler initialized');
  }

  private detectChromeExtension() {
    // Check if we're in an iframe
    const isInIframe = window !== window.parent;
    
    // Check URL parameters for Chrome extension indicators
    const urlParams = new URLSearchParams(window.location.search);
    const isChromeParam = urlParams.get('chrome') === 'true' || 
                         urlParams.get('extension') === 'true' ||
                         urlParams.get('isChromeExtension') === 'true';

    // Check referrer for Chrome extension indicators
    const referrer = document.referrer;
    const isChromeReferrer = referrer.includes('chrome-extension://') || 
                            referrer.includes('moz-extension://');

    // Safe check for Chrome extension context
    let hasChromeExtensionContext = false;
    try {
      // Check if chrome.runtime exists (without causing TypeScript errors)
      hasChromeExtensionContext = !!(
        (window as any).chrome && 
        (window as any).chrome.runtime &&
        typeof (window as any).chrome.runtime === 'object'
      );
    } catch (error) {
      // Chrome extension APIs not available
      hasChromeExtensionContext = false;
    }

    // Additional check: if we're in a side panel context
    const isSidePanel = window.location.href.includes('chrome-extension://') ||
                       window.location.protocol === 'chrome-extension:';

    if (isInIframe || isChromeParam || isChromeReferrer || hasChromeExtensionContext || isSidePanel) {
      this.isChromeExtension = true;
      
      // Send initialization message to parent frame
      this.sendToParent({
        type: 'CHROME_EXTENSION_INIT',
        isChromeExtension: true
      });

      console.log('Chrome Extension context detected', {
        isInIframe,
        isChromeParam,
        isChromeReferrer,
        hasChromeExtensionContext,
        isSidePanel
      });
    }
  }

  private handleMessage(event: MessageEvent) {
    // Verify origin for security with development support
    const allowedOrigins = [
      window.location.origin,
      'chrome-extension://',
      'moz-extension://',
      'http://localhost',
      'http://127.0.0.1'
    ];

    const isAllowedOrigin = allowedOrigins.some(origin => 
      event.origin.startsWith(origin) || event.origin === 'null'
    );

    // Additional check for development localhost ports
    const isLocalhost = event.origin.match(/^https?:\/\/(localhost|127\.0\.0\.1):\d+$/);

    if (!isAllowedOrigin && !isLocalhost) {
      console.warn('Message from unauthorized origin:', event.origin);
      return;
    }

    try {
      const message = event.data as ChromeExtensionMessage;
      
      if (message && typeof message === 'object' && message.type) {
        console.log('Received Chrome extension message:', message);
        this.notifyListeners(message);
      }
    } catch (error) {
      console.error('Error handling Chrome extension message:', error);
    }
  }

  private sendToParent(message: ChromeExtensionMessage) {
    if (window !== window.parent) {
      window.parent.postMessage(message, '*');
    }
  }

  public addListener(callback: (message: ChromeExtensionMessage) => void) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(message: ChromeExtensionMessage) {
    this.listeners.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        console.error('Error in Chrome extension message listener:', error);
      }
    });
  }

  public sendScrapedContent(content: ScrapedPageContent) {
    this.sendToParent({
      type: 'PAGE_CONTENT_SCRAPED',
      data: content
    });
  }

  public isChromeExtensionContext(): boolean {
    return this.isChromeExtension;
  }

  // Helper method to create a file from scraped content
  public static createFileFromScrapedContent(scrapedContent: ScrapedPageContent): File {
    const { url, title, content } = scrapedContent;
    const meta = content.meta || {};
    
    // Extract domain from URL
    let domain = '';
    try {
      domain = new URL(url).hostname;
    } catch (e) {
      domain = 'unknown';
    }
    
    // Handle readableContent which might be a string or object
    let readableText = '';
    if (typeof content.readableContent === 'string') {
      readableText = content.readableContent;
    } else if (content.readableContent && typeof content.readableContent === 'object') {
      readableText = content.readableContent.flowing || 'No readable content available';
    } else {
      readableText = 'No readable content available';
    }
    
    // Create formatted content
    const formattedContent = `URL: ${url || 'N/A'}
Title: ${title || 'N/A'}
Domain: ${domain}
Scraped at: ${scrapedContent.timestamp || new Date().toISOString()}

Description: ${meta.description || 'N/A'}
Keywords: ${meta.keywords || 'N/A'}
Author: ${meta.author || 'N/A'}

Content:
${readableText}

Document Structure:
${JSON.stringify(content.documentStructure, null, 2)}`;

    // Create filename from title (sanitized)
    const sanitizedTitle = (title || 'scraped_content').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    const filename = `${sanitizedTitle || 'scraped_content'}.txt`;

    // Create File object
    return new File([formattedContent], filename, {
      type: 'text/plain',
      lastModified: Date.now()
    });
  }
}

export default ChromeExtensionHandler;